import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import useSeo from "@/lib/seo";
import { api, formatGBP } from "@/lib/api";
import { findGuide } from "@/lib/content";
import { Calendar, Clock, Tent, ArrowRight, ExternalLink, Star, HelpCircle, MapPin } from "lucide-react";

/**
 * Data-driven guide page used by every entry in GUIDES with kind === "dynamic".
 * Reads `intro` (string[] of paragraphs), `picks` (array of {slug, why}), and
 * `faqs` (array of {q, a}) from the GUIDES entry and renders a fully SEO-ready
 * article. Park data is loaded live from /api/parks/{slug} so prices and ratings
 * stay current.
 */
export default function DynamicGuide() {
  const { slug } = useParams();
  const guide = findGuide(slug);
  const [picks, setPicks] = useState([]);

  useSeo({
    title: guide?.title,
    description: guide?.description,
    keywords: guide?.keywords,
    canonical: typeof window !== "undefined" && guide ? `${window.location.origin}/guides/${guide.slug}` : undefined,
  });

  useEffect(() => {
    if (!guide?.picks) return;
    Promise.all(guide.picks.map((p) => api.getPark(p.slug).catch(() => null))).then((parks) => {
      setPicks(guide.picks.map((p, i) => ({ ...p, park: parks[i] })));
    });
  }, [guide]);

  if (!guide) return <Navigate to="/guides" replace />;

  return (
    <div className="App">
      <Nav />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid={`guide-page-${guide.slug}`}>
        <Link to="/guides" className="text-sm text-forest hover:underline" data-testid="guide-back-link">← All guides</Link>

        <header className="mt-6">
          <span className="label-eyebrow">{guide.category}</span>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl mt-2 leading-tight tracking-tight" data-testid="guide-title">
            {guide.title}
          </h1>
          <div className="flex flex-wrap items-center gap-3 text-sm text-ink/60 mt-4">
            <span className="inline-flex items-center gap-1"><Calendar className="w-4 h-4" />{new Date(guide.publishedAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</span>
            <span className="text-ink/30">·</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-4 h-4" />{guide.readMinutes} min read</span>
            <span className="text-ink/30">·</span>
            <span className="inline-flex items-center gap-1"><Tent className="w-4 h-4" />ParkScanner Editorial</span>
          </div>
        </header>

        <div className="aspect-[16/9] rounded-2xl bg-gradient-to-br from-forest via-forest-light to-sky mt-8 flex items-center justify-center text-9xl shadow-lg" aria-hidden="true">
          {guide.heroEmoji}
        </div>

        <div className="prose prose-lg max-w-none mt-10 text-ink/85 leading-relaxed space-y-5" data-testid="guide-intro">
          {(guide.intro || []).map((p, i) => (
            <p key={i} className={i === 0 ? "text-xl font-display text-ink" : ""}>{p}</p>
          ))}
        </div>

        <h2 className="font-display font-bold text-3xl mt-14 mb-2" data-testid="guide-picks-heading">Our top picks</h2>
        <p className="text-ink/60 text-sm mb-8">Live indicative prices for 2 adults, refreshed hourly. Tap any park to confirm availability on the operator's own site.</p>

        <div className="space-y-12" data-testid="guide-picks-list">
          {picks.map(({ slug: pickSlug, why, park }, idx) => (
            <section key={pickSlug} className="border-t border-border pt-10" data-testid={`pick-${idx + 1}`}>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-forest text-white flex items-center justify-center shrink-0">
                  <span className="font-display font-extrabold text-xl">{idx + 1}</span>
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-ink/60">
                    <span className="label-eyebrow">{park?.operator || "…"}</span>
                    <span className="text-ink/30">·</span>
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{park?.region || ""}</span>
                  </div>
                  <h3 className="font-display font-bold text-2xl sm:text-3xl mt-1">{park?.name || "Loading…"}</h3>
                </div>
              </div>

              <p className="text-ink/85 leading-relaxed text-lg">{why}</p>

              {park && (
                <div className="mt-6 bg-white border border-border rounded-2xl p-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm">
                      <Star className="w-4 h-4 fill-terracotta text-terracotta" />
                      <span className="font-semibold">{park.rating}</span>
                      <span className="text-ink/60">({park.review_count.toLocaleString()} reviews)</span>
                    </div>
                    <div className="text-xs uppercase tracking-wider text-ink/50 mt-2">indicative from</div>
                    <div className="font-display text-2xl font-extrabold text-forest">
                      {formatGBP(park.pricing.per_night)}<span className="text-sm font-medium text-ink/60"> /night</span>
                    </div>
                  </div>
                  <Link to={`/park/${park.slug}`} className="btn-ghost text-sm" data-testid={`pick-park-link-${idx + 1}`}>
                    View park <ArrowRight className="w-4 h-4" />
                  </Link>
                  <a
                    href={park.booking_url}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    className="btn-primary text-sm"
                    data-testid={`pick-park-deal-${idx + 1}`}
                  >
                    Check availability <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
            </section>
          ))}
        </div>

        {guide.faqs && guide.faqs.length > 0 && (
          <section className="mt-16 bg-sand border border-border rounded-2xl p-6 sm:p-8" data-testid="guide-faqs">
            <div className="flex items-center gap-2 mb-2">
              <HelpCircle className="w-5 h-5 text-forest" />
              <span className="label-eyebrow">FAQ</span>
            </div>
            <h2 className="font-display font-bold text-2xl sm:text-3xl">Questions before you book</h2>
            <div className="mt-6 divide-y divide-border">
              {guide.faqs.map((f, i) => (
                <details key={i} className="py-4 group" data-testid={`faq-${i}`}>
                  <summary className="cursor-pointer font-display font-semibold text-lg list-none flex items-center justify-between gap-4">
                    <span>{f.q}</span>
                    <span className="text-forest font-light text-2xl group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="text-ink/75 mt-3 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        <section className="mt-12 bg-forest text-white border border-border rounded-2xl p-6 text-center">
          <h3 className="font-display font-bold text-2xl">Compare all 180+ UK parks live</h3>
          <p className="text-white/80 mt-2 text-sm">Including the picks above plus 175 more — filter by region, facilities and budget.</p>
          <Link to="/search" className="inline-flex items-center gap-2 mt-4 bg-white text-forest rounded-full px-5 py-2.5 font-semibold hover:bg-sand transition-colors" data-testid="guide-cta-search">
            Start your search <ArrowRight className="w-4 h-4" />
          </Link>
        </section>

        {/* JSON-LD FAQPage schema for richer Google snippets */}
        {guide.faqs && guide.faqs.length > 0 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: guide.faqs.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              }),
            }}
          />
        )}
      </article>

      <Footer />
    </div>
  );
}
